import React, { useState, useEffect } from 'react';
import { FileCheck, AlertCircle, Shield, Clock, DollarSign, Scale, Lock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { useToast } from '@/hooks/errors/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import GlassPill from '@/components/ui/GlassPill';

interface ServiceAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrderId: string;
  workOrderName: string;
  projectId?: string;
  onAgreementSigned?: () => void;
}

// Standard service agreement terms
const AGREEMENT_TERMS = {
  version: "2.0",
  lastUpdated: "2024-12-15",
  sections: [
    {
      title: "1. Agreement Framework",
      icon: Scale,
      content: [
        "This Agreement governs all services provided by us (\"Developer\") to you (\"Customer\")",
        "Future work will be documented in Statements of Work (SOWs) referencing these terms"
      ]
    },
    {
      title: "2. Change Management",
      icon: Users,
      content: [
        "Either party may request changes via written Change Request",
        "Changes become binding only when agreed in writing by both parties",
        "Work commenced before written agreement is billable at current rates"
      ]
    },
    {
      title: "3. Intellectual Property & Deliverables",
      icon: Shield,
      content: [
        "Developer retains ownership of all created IP, except third-party and customer-provided materials",
        "Customer receives a perpetual, worldwide, royalty-free license to use, modify, and distribute all deliverables",
        "Source Code delivered in Git format with build scripts, configurations, and documentation",
        "Third-party components subject to their original licenses (to be disclosed before integration)",
        "Documentation provided as specified deliverable for all custom software",
        "Developer may reuse non-confidential components for other projects"
      ]
    },
    {
      title: "3A. Service Classifications & Scope",
      icon: Users,
      content: [
        "One-time Services: Delivered upon completion with defined acceptance criteria in SOW",
        "Ongoing Services: Billed monthly/periodically with separate termination terms",
        "Support Services: Subject to business hours and response time agreements",
        "Service descriptions are capabilities, not guaranteed outcomes or performance metrics",
        "Each service limited to scope defined in relevant SOW"
      ]
    },
    {
      title: "3B. Third-Party Dependencies",
      icon: Shield,
      content: [
        "Customer responsible for all third-party service subscriptions, licenses, and API access",
        "Developer not liable for changes, deprecation, or failures of external platforms, APIs, or services",
        "Integrations subject to and dependent on third-party terms and continued availability",
        "Customer must maintain accounts and access for all integrated services"
      ]
    },
    {
      title: "4. Customer Obligations",
      icon: Users,
      content: [
        "Customer must provide necessary cooperation, information, and documentation to enable service delivery",
        "Delays in customer obligations may impact delivery timelines"
      ]
    },
    {
      title: "5. Warranties",
      icon: Shield,
      content: [
        "Legal authority to perform services",
        "Compliance with applicable laws",
        "Software free from defects for 12 months",
        "No infringement of third-party IP rights",
        "Professional standard of work",
        "Security features reflecting good industry practice",
        "No warranty on third-party service availability or performance"
      ]
    },
    {
      title: "6. Liability Limitations",
      icon: Scale,
      content: [
        "Maximum liability per incident: Greater of $5,000 or 12 months' fees",
        "Total aggregate liability: Greater of $10,000 or total contract value",
        "No liability for indirect, consequential damages or loss of profits",
        "No liability for third-party service failures or changes",
        "Excludes liability for negligence, fraud, or statutory obligations"
      ]
    },
    {
      title: "7. Payment Terms",
      icon: DollarSign,
      content: [
        "Invoices payable within 14 days",
        "8% annual interest on overdue amounts",
        "50% deposit required upfront, balance on completion",
        "Developer may suspend work for overdue payments",
        "Customer liable for collection costs including legal fees",
        "Ongoing services billed monthly in advance"
      ]
    },
    {
      title: "8. Termination & Breach Remedies",
      icon: Scale,
      content: [
        "Either party: 30 days' written notice for convenience",
        "Immediate termination for non-remediable material breach",
        "30 days to cure remediable breaches after written notice",
        "Immediate termination for persistent breaches (even if individually minor)",
        "Non-payment: 30-day notice before termination, work may be suspended immediately",
        "Immediate termination for insolvency or bankruptcy",
        "Ongoing services may have separate termination terms in SOW"
      ]
    },
    {
      title: "9. General Terms",
      icon: Scale,
      content: [
        "Subcontracting permitted with Developer retaining full responsibility",
        "Amendments require written agreement",
        "No third-party beneficiaries",
        "Entire agreement supersedes previous understandings",
        "Governed by Australian law",
        "Queensland courts' exclusive jurisdiction"
      ]
    },
    {
      title: "10. Force Majeure",
      icon: Shield,
      content: [
        "Neither party liable for delays due to events beyond reasonable control (natural disasters, pandemics, cyber attacks, third-party service outages, etc.)",
        "Affected party must notify promptly and mitigate effects"
      ]
    },
    {
      title: "11. Surviving Provisions",
      icon: Lock,
      content: [
        "Upon termination, the following survive: payment obligations, IP rights, liability limitations, confidentiality, data protection obligations, and dispute resolution"
      ]
    },
    {
      title: "12. Data Protection & Privacy",
      icon: Lock,
      content: [
        "Customer remains data controller for all personal data processed",
        "Developer acts solely as data processor under Customer's instructions",
        "Customer responsible for legal basis for all data processing",
        "Customer must ensure compliance with all applicable privacy laws",
        "Developer implements reasonable security measures but does not guarantee against all breaches",
        "Each party responsible for its own compliance with data protection laws",
        "Customer indemnifies Developer for any data protection violations arising from Customer's instructions"
      ]
    }
  ]
};

export const ServiceAgreementModal: React.FC<ServiceAgreementModalProps> = ({
  isOpen,
  onClose,
  workOrderId,
  workOrderName,
  projectId,
  onAgreementSigned,
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [existingAgreement, setExistingAgreement] = useState<any>(null);
  const [agreementSignedBy, setAgreementSignedBy] = useState<string | null>(null);
  const [agreementSignedAt, setAgreementSignedAt] = useState<string | null>(null);
  const [personalInfo, setPersonalInfo] = useState<any>(null);
  const [companyInfo, setCompanyInfo] = useState<any>(null);

  // Load existing agreement when modal opens
  useEffect(() => {
    if (isOpen && workOrderId) {
      loadPersonalAndCompanyInfo();
      loadExistingAgreement();
    }
  }, [isOpen, workOrderId]);

  const loadPersonalAndCompanyInfo = async () => {
    try {
      // Load personal info
      if (user?.id) {
        const { data: userData } = await supabase
          .from('users')
          .select('first_name, last_name, email')
          .eq('id', user.id)
          .single();
        
        if (userData) {
          setPersonalInfo(userData);
        }
      }
      
      // Load company info if projectId is provided
      if (projectId) {
        const { data: workbookData } = await supabase
          .from('workbook')
          .select('company_info')
          .eq('id', projectId)
          .single();
        
        if (workbookData?.company_info) {
          setCompanyInfo(workbookData.company_info);
        }
      }
    } catch (error) {
      console.error('Error loading personal/company info:', error);
    }
  };

  const loadExistingAgreement = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('workbook_services')
        .select('service_agreement, agreement_signed_by, agreement_signed_at')
        .eq('id', workOrderId)
        .single();

      if (error) throw error;
      
      if (data?.agreement_signed_by) {
        setExistingAgreement(data.service_agreement);
        setAgreementSignedAt(data.agreement_signed_at);
        
        // Check if the agreement has signatory_details with personal_info
        if (data.service_agreement?.signatory_details?.personal_info) {
          const signatoryInfo = data.service_agreement.signatory_details.personal_info;
          const fullName = signatoryInfo.first_name && signatoryInfo.last_name 
            ? `${signatoryInfo.first_name} ${signatoryInfo.last_name}`
            : signatoryInfo.email || data.agreement_signed_by;
          setAgreementSignedBy(fullName);
        } else {
          // Fallback: Get the user's name who signed from users table
          const { data: userData } = await supabase
            .from('users')
            .select('first_name, last_name, email')
            .eq('id', data.agreement_signed_by)
            .single();
            
          if (userData) {
            const fullName = userData.first_name && userData.last_name 
              ? `${userData.first_name} ${userData.last_name}`
              : userData.email || data.agreement_signed_by;
            setAgreementSignedBy(fullName);
          } else {
            setAgreementSignedBy(data.agreement_signed_by);
          }
        }
      }
    } catch (error) {
      console.error('Error loading agreement:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignAgreement = async () => {
    if (!hasAgreed) {
      toast({
        title: 'Agreement Required',
        description: 'Please check the box to confirm you have read and agree to the terms',
        variant: 'destructive',
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: 'Authentication Required',
        description: 'You must be logged in to sign the agreement',
        variant: 'destructive',
      });
      return;
    }

    setIsSigning(true);
    try {
      const agreementData = {
        ...AGREEMENT_TERMS,
        agreed_to_at: new Date().toISOString(),
        work_order_id: workOrderId,
        work_order_name: workOrderName,
        signatory_details: {
          personal_info: {
            first_name: personalInfo?.first_name,
            last_name: personalInfo?.last_name,
            email: personalInfo?.email || user?.email,
            user_id: user?.id
          },
          company_info: companyInfo ? {
            company_name: companyInfo.company_name,
            abn: companyInfo.abn,
            registered_address: companyInfo.registered_address,
            contact_email: companyInfo.contact_email,
            contact_phone: companyInfo.contact_phone,
            website: companyInfo.website
          } : null,
          signed_at: new Date().toISOString(),
          ip_address: null, // Could be captured if needed
          user_agent: navigator.userAgent
        }
      };

      const { error } = await supabase
        .from('workbook_services')
        .update({ 
          service_agreement: agreementData,
          agreement_signed_by: user.id,
          agreement_signed_at: new Date().toISOString()
        })
        .eq('id', workOrderId);

      if (error) throw error;

      toast({
        title: 'Agreement Signed',
        description: 'Service agreement has been successfully signed',
        variant: 'default',
      });

      onAgreementSigned?.();
      onClose();
    } catch (error) {
      console.error('Error signing agreement:', error);
      toast({
        title: 'Error',
        description: 'Failed to sign agreement',
        variant: 'destructive',
      });
    } finally {
      setIsSigning(false);
    }
  };

  const isAlreadySigned = !!agreementSignedBy;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isAlreadySigned ? (
              <FileCheck className="h-5 w-5 text-green-500" />
            ) : (
              <FileCheck className="h-5 w-5 text-purple-500" />
            )}
            Service Agreement {isAlreadySigned && <GlassPill type="Signed" variant="green" />}
          </DialogTitle>
          <DialogDescription>
            {isAlreadySigned 
              ? `${workOrderName}`
              : `Please review and accept the service agreement for ${workOrderName}`
            }
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4 p-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {isAlreadySigned && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-700 dark:text-green-400">Signed by:</span>
                  <span className="font-medium text-green-900 dark:text-green-100">{agreementSignedBy}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-green-700 dark:text-green-400">Date:</span>
                  <span className="font-medium text-green-900 dark:text-green-100">{new Date(agreementSignedAt!).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
              </div>
            )}

            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-6">
                {/* Agreement Header */}
                <div className="text-left py-2 border-b">
                  <h3 className="text-lg font-semibold">Service Agreement Terms</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Version {AGREEMENT_TERMS.version} • Last Updated: {AGREEMENT_TERMS.lastUpdated}
                  </p>
                </div>

                {/* Agreement Sections */}
                {AGREEMENT_TERMS.sections.map((section, index) => {
                  const Icon = section.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="space-y-3"
                    >
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                          <Icon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <h4 className="font-semibold text-base">{section.title}</h4>
                      </div>
                      <ul className="space-y-2 ml-10">
                        {section.content.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start gap-2">
                            <span className="text-purple-500 mt-0.5">•</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  );
                })}

                {/* Agreement Footer */}
                <div className="pt-6 border-t">
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div className="text-sm text-amber-900 dark:text-amber-100 space-y-3">
                        <p className="font-medium">Important Notice - Legal Commitment</p>
                        
                        <div className="space-y-2">
                          <p className="font-medium">By signing, you confirm:</p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>You're authorized to sign for {companyInfo?.company_name || 'your company'}</li>
                            <li>The details above are correct</li>
                            <li>You accept these terms on behalf of the company</li>
                          </ul>
                        </div>
                        
                        <p className="border-t pt-2">
                          This Agreement, together with the specific SOW, constitutes the entire agreement 
                          between the parties. Service descriptions are illustrative of capabilities and do 
                          not constitute performance guarantees.
                        </p>
                        
                        {!isAlreadySigned && personalInfo && companyInfo && (
                          <div className="bg-white/50 dark:bg-gray-800/50 rounded p-2 space-y-1 mt-2">
                            <p><strong>Signed By:</strong> {personalInfo.first_name} {personalInfo.last_name}</p>
                            <p><strong>Email:</strong> {personalInfo.email || user?.email}</p>
                            <p><strong>On behalf of:</strong> {companyInfo.company_name}</p>
                            <p><strong>ABN:</strong> {companyInfo.abn}</p>
                          </div>
                        )}
                        {isAlreadySigned && existingAgreement?.signatory_details && (
                          <div className="bg-white/50 dark:bg-gray-800/50 rounded p-2 space-y-1 mt-2">
                            <p><strong>Signed By:</strong> {existingAgreement.signatory_details.personal_info?.first_name} {existingAgreement.signatory_details.personal_info?.last_name}</p>
                            <p><strong>Email:</strong> {existingAgreement.signatory_details.personal_info?.email}</p>
                            {existingAgreement.signatory_details.company_info && (
                              <>
                                <p><strong>On behalf of:</strong> {existingAgreement.signatory_details.company_info.company_name}</p>
                                <p><strong>ABN:</strong> {existingAgreement.signatory_details.company_info.abn}</p>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>

            {!isAlreadySigned && (
              <div className="flex items-center space-x-2 pt-4 border-t">
                <Checkbox
                  id="agree"
                  checked={hasAgreed}
                  onCheckedChange={(checked) => setHasAgreed(checked as boolean)}
                />
                <label
                  htmlFor="agree"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I agree to these terms on behalf of {companyInfo?.company_name || 'the company'}
                </label>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {isAlreadySigned ? 'Close' : 'Cancel'}
          </Button>
          {!isAlreadySigned && (
            <Button
              onClick={handleSignAgreement}
              disabled={!hasAgreed || isSigning}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
            >
              {isSigning ? 'Signing...' : 'Sign Agreement'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};